import React, { Ref, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from '@emotion/styled';
import {
  CUSTOMER_AGREEMENT_URL,
  CUSTOMER_AGREEMENT_URL_MOBILE,
  PRIVACY_POLICY_URL,
  PRIVACY_POLICY_URL_MOBILE,
} from './links';
import { useLinkClickedEvent } from '../utilities/analytics/analytics-web-client';
import Link from '@atlaskit/link';

interface Props {
  isEmbedded: boolean;
  isMobileOidc: boolean;
  pageId: string;
  privacyPolicyText: PrivacyPolicyText;
}

const SignupLegal = styled.p`
  font-size: 12px;
`;

export enum PrivacyPolicyText {
  emailCapture = 'emailCapture',
  passwordSignup = 'passwordSignup',
  oauthSignup = 'oauthSignup',
}

const PrivacyPolicy = React.forwardRef(
  (
    { isEmbedded, isMobileOidc, pageId, privacyPolicyText }: Props,
    ref: Ref<HTMLParagraphElement>
  ) => {
    const customerAgreementLinkClicked = useLinkClickedEvent(pageId, 'cloudTermsOfService');
    const privacyPolicyLinkClicked = useLinkClickedEvent(pageId, 'privacyPolicy');

    const privacyPolicyMessage = useMemo(() => {
      const shouldUseMobileLinks = isEmbedded || isMobileOidc;
      const privacyPolicyUrl = shouldUseMobileLinks
        ? PRIVACY_POLICY_URL_MOBILE
        : PRIVACY_POLICY_URL;
      const customerAgreementUrl = shouldUseMobileLinks
        ? CUSTOMER_AGREEMENT_URL_MOBILE
        : CUSTOMER_AGREEMENT_URL;

      const messageValues = {
        'customer-agreement-link': chunks => (
          <Link
            href={customerAgreementUrl}
            target="_blank"
            rel="noreferrer noopener"
            onClick={customerAgreementLinkClicked}>
            {chunks}
          </Link>
        ),
        'privacy-policy-link': chunks => (
          <Link
            href={privacyPolicyUrl}
            target="_blank"
            rel="noreferrer noopener"
            onClick={privacyPolicyLinkClicked}>
            {chunks}
          </Link>
        ),
      };

      switch (privacyPolicyText) {
        case PrivacyPolicyText.emailCapture:
          return (
            <FormattedMessage
              id="signup.form.legal.email.capture.xml"
              defaultMessage="By clicking 'Sign up', you acknowledge the <privacy-policy-link>Atlassian Privacy Policy</privacy-policy-link>."
              description="Privacy Policy notice displayed before a user's email address is submitted"
              values={messageValues}
            />
          );
        case PrivacyPolicyText.passwordSignup:
          return (
            <FormattedMessage
              id="signup.form.legal.customer.agreement.password.xml"
              defaultMessage="By clicking 'Continue', you agree to the <customer-agreement-link>Atlassian Customer Agreement</customer-agreement-link> and acknowledge the <privacy-policy-link>Atlassian Privacy Policy</privacy-policy-link>."
              description="Customer Agreement and Privacy Policy notice displayed before an Atlassian account is finalized"
              values={messageValues}
            />
          );
        case PrivacyPolicyText.oauthSignup:
          return (
            <FormattedMessage
              id="signup.form.legal.customer.agreement.oauth.xml"
              defaultMessage="By clicking 'Create your account', you agree to the <customer-agreement-link>Atlassian Customer Agreement</customer-agreement-link> and acknowledge the <privacy-policy-link>Atlassian Privacy Policy</privacy-policy-link>."
              description="Customer Agreement and Privacy Policy notice displayed before an Atlassian account is created"
              values={messageValues}
            />
          );
      }
    }, [
      customerAgreementLinkClicked,
      privacyPolicyLinkClicked,
      isEmbedded,
      isMobileOidc,
      privacyPolicyText,
    ]);

    return (
      <SignupLegal data-testid="legal-message" ref={ref}>
        {privacyPolicyMessage}
      </SignupLegal>
    );
  }
);

PrivacyPolicy.displayName = 'PrivacyPolicy';

export default PrivacyPolicy;
